import { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import type {
	Team,
	User,
	Project,
	CreateUserRequest,
	CreateProjectRequest,
	SetProjectMembersRequest,
} from '@spirtech/shared';

export function useTeams() {
	const [teams, setTeams] = useState<Team[]>([]);
	const [loading, setLoading] = useState(false);

	const refresh = useCallback(async () => {
		setLoading(true);
		try {
			setTeams(await api.get<Team[]>('/admin/teams'));
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		refresh();
	}, []);

	const createTeam = async (name: string) => {
		await api.post('/admin/teams', { name });
		await refresh();
	};
	const updateTeam = async (id: number, name: string) => {
		await api.patch(`/admin/teams/${id}`, { name });
		await refresh();
	};

	return { teams, loading, createTeam, updateTeam };
}

export function useCategories() {
	const [categories, setCategories] = useState<{ id: number; name: string }[]>([]);
	const [loading, setLoading] = useState(false);

	const refresh = useCallback(async () => {
		setLoading(true);
		try {
			setCategories(await api.get<{ id: number; name: string }[]>('/admin/categories'));
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		refresh();
	}, []);

	const createCategory = async (name: string) => {
		await api.post('/admin/categories', { name });
		await refresh();
	};
	const updateCategory = async (id: number, name: string) => {
		await api.patch(`/admin/categories/${id}`, { name });
		await refresh();
	};
	const deleteCategory = async (id: number) => {
		await api.delete(`/admin/categories/${id}`);
		await refresh();
	};

	return { categories, loading, createCategory, updateCategory, deleteCategory };
}

export function useUsers() {
	const [users, setUsers] = useState<User[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const refresh = useCallback(async () => {
		setLoading(true);
		try {
			setUsers(await api.get<User[]>('/admin/users'));
		} catch (e: any) {
			setError(e.message);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		refresh();
	}, []);

	const createUser = async (body: CreateUserRequest) => {
		await api.post('/admin/users', body);
		await refresh();
	};
	const updateUser = async (id: number, patch: Partial<User>) => {
		await api.patch(`/admin/users/${id}`, patch);
		await refresh();
	};
	// Re-send the passwordless welcome email. 204 on success; 502 with
	// { error: 'mail_send_failed' } when SMTP is unhealthy.
	const resendWelcome = async (id: number) => {
		await api.post(`/admin/users/${id}/welcome`, {});
	};

	return { users, loading, error, createUser, updateUser, resendWelcome, refresh };
}

export function useProjects() {
	const [projects, setProjects] = useState<Project[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const refresh = useCallback(async () => {
		setLoading(true);
		try {
			setProjects(await api.get<Project[]>('/admin/projects'));
		} catch (e: any) {
			setError(e.message);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		refresh();
	}, []);

	const createProject = async (body: CreateProjectRequest) => {
		await api.post('/admin/projects', body);
		await refresh();
	};
	const updateProject = async (id: number, patch: Partial<Project>) => {
		await api.patch(`/admin/projects/${id}`, patch);
		await refresh();
	};
	const getMembers = async (projectId: number): Promise<{ id: number; isResponsible: boolean }[]> =>
		api.get<{ id: number; isResponsible: boolean }[]>(`/admin/projects/${projectId}/members`);
	const setMembers = async (projectId: number, body: SetProjectMembersRequest) =>
		api.put(`/admin/projects/${projectId}/members`, body);
	/* Merge the source project into the target. Backend re-parents tasks (or
	   merges them into a same-named target task), migrates members, and drops
	   the source project row. Reloads the project list on success. */
	const mergeProjects = async (sourceProjectId: number, intoProjectId: number) => {
		const vRes = await api.post<{ ok: true; mergedInto: number; movedTasks: number; mergedTasks: number }>(
			`/admin/projects/${sourceProjectId}/merge`,
			{ intoProjectId },
		);
		await refresh();
		return vRes;
	};

	return { projects, loading, error, createProject, updateProject, getMembers, setMembers, mergeProjects, refresh };
}

/* Hook backing the Admin → Tâches merge panel. Loads tasks of a single project
   on demand; mergeTasks calls POST /admin/tasks/:id/merge and reloads. */
export function useAdminTasks() {
	const getTasks = async (projectId: number) =>
		api.get<{ id: number; name: string; isActive: boolean; createdBy: number }[]>(
			`/admin/tasks?projectId=${projectId}`,
		);

	const mergeTasks = async (sourceTaskId: number, intoTaskId: number) =>
		api.post<{ ok: true; mergedInto: number }>(`/admin/tasks/${sourceTaskId}/merge`, { intoTaskId });

	return { getTasks, mergeTasks };
}

export interface AuditEntry {
	id: number;
	userId: number;
	userInitials: string;
	userFullName: string;
	action: string;
	targetType: string;
	targetId: number | null;
	payload: unknown;
	createdAt: string;
}

export function useAudit() {
	const [entries, setEntries] = useState<AuditEntry[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const refresh = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			setEntries(await api.get<AuditEntry[]>('/admin/audit'));
		} catch (e: any) {
			setError(e.message);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		refresh();
	}, [refresh]);

	return { entries, loading, error, refresh };
}
